
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import bgNightCycling from './assets/bg_night_cycling.png';
import healthHeaderBg from './assets/health_header_bg.png';
import heroSaludBg from './assets/hero_salud.png';
// HMR FORCE 1
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import HealthSidebar from './components/HealthSidebar';
import GoalsWidget from './components/GoalsWidget';
import GoalCreatorModal from './components/GoalCreatorModal';
import WhatsNewAiModal from './components/WhatsNewAiModal';
import RecoveryPanel from './components/RecoveryPanel';
import TrainingCalendar from './components/TrainingCalendar';
import { syncHealthData } from './utils/health';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import LastActivity from './components/LastActivity';
import RecentActivities from './components/RecentActivities';
import TrainingReadiness from './components/TrainingReadiness';
import AerobicEfficiency from './components/AerobicEfficiency';
import MorningBriefingBanner from './components/MorningBriefingBanner';
import DailyFeedbackWidget from './components/DailyFeedbackWidget';
import BiomechanicsCard from './components/BiomechanicsCard';
import MetabolismCard from './components/MetabolismCard';

// LAZY LOADED COMPONENTS
const CurrentYearMonthlyStats = React.lazy(() => import('./components/CurrentYearMonthlyStats'));
const IntensityChart = React.lazy(() => import('./components/IntensityChart'));
const ActivityBreakdown = React.lazy(() => import('./components/ActivityBreakdown'));
const FitnessChart = React.lazy(() => import('./components/FitnessChart'));
const PerformanceEvolution = React.lazy(() => import('./components/PerformanceEvolution'));
const HeartRateDistribution = React.lazy(() => import('./components/HeartRateDistribution'));
const HistoricalStats = React.lazy(() => import('./components/HistoricalStats'));
import AICoachDrawer from './components/AICoachDrawer';
// import AIReportCard from './components/AIReportCard';
import EmptyState from './components/EmptyState';
import TabHeaderBanner from './components/TabHeaderBanner';
import UpcomingEvents from './components/UpcomingEvents';
import EventModal from './components/EventModal';
import YearToDateWidget from './components/YearToDateWidget';

import AccountSettingsModal from './components/AccountSettingsModal';
import PricingModal from './components/PricingModal';
import { initPurchases } from './utils/purchases';
import FitUploadModal from './components/FitUploadModal';
import CoachFatigueAlert from './components/CoachFatigueAlert';
import DailyHealthModal from './components/DailyHealthModal';
import CoachDashboard from './components/CoachDashboard';
import AdminDashboard from "./components/AdminDashboard";
import AthleteHeader from './components/AthleteHeader';
import CoachAthleteMessaging from './components/CoachAthleteMessaging';
import AiAnalysisModal from './components/AiAnalysisModal';
import AthleteProfileCard from './components/AthleteProfileCard';
import Login from './components/Login';
import MessagingPanel from './components/MessagingPanel';
import NotificationsPanel from './components/NotificationsPanel';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Home, Activity, Heart, Sun, Moon, Settings, Bell, LogOut, User, Plus, Target, Zap, HelpCircle, LineChart, MessageSquare, MessageCircle, X, Sparkles, Calendar, RefreshCw, Upload } from 'lucide-react';
import OnboardingWizard from './components/OnboardingWizard';
import ResetPassword from './components/ResetPassword';
import CoachOnboardingWizard from './components/CoachOnboardingWizard';
import AthleteHelpTour from './components/AthleteHelpTour';
import CoachHelpTour from './components/CoachHelpTour';
import BetaFeedbackModal from './components/BetaFeedbackModal';
import SomaLandingPage from './components/SomaLandingPage';
import CookieBanner from './components/CookieBanner';
import { getLocalQuote, getSimulatedWeather } from './utils/quotesHelper';
import NousLogo from './components/NousLogo';
import NativeSplash from './components/NativeSplash';
import './index.css';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const fetchOptions = { ...options, headers, credentials: 'include' };
  const res = await fetch(`${API_URL}${path}`, fetchOptions);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    if (res.status === 401 && (errorData.error === 'Falta token de autenticación' || errorData.error === 'Token inválido o expirado')) {
      throw new Error('NO_AUTH');
    }
    throw new Error(errorData.error || `Server returned status ${res.status}`);
  }
  const data = await res.json();
  console.log(`[API] ${path} returned`, data);
  return data;
}

async function fetchSafe(path, fallback) {
  try {
    return await apiFetch(path);
  } catch (e) {
    if (e.message === 'NO_AUTH') throw e;
    console.warn(`[SafeFetch] Failed to fetch ${path}, using fallback:`, e.message);
    return fallback;
  }
}

const HEADER_BACKGROUNDS = {
  morning: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?fm=jpg&auto=compress&fit=crop&q=80&w=1600', // Amanecer / Golden Hour Trail Running
  midday: 'https://images.unsplash.com/photo-1502224562085-639556652f33?fm=jpg&auto=compress&fit=crop&q=80&w=1600',  // Mediodía / Pista atlética soleada
  sunset: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?fm=jpg&auto=compress&fit=crop&q=80&w=1600',  // Atardecer espectacular / Entrenamiento
  night: bgNightCycling    // Noche / Ciclismo Carretera
};

const getBackgroundForHour = (hour) => {
  if (hour >= 5 && hour < 12) return HEADER_BACKGROUNDS.morning;
  if (hour >= 12 && hour < 19) return HEADER_BACKGROUNDS.midday;
  if (hour >= 19 && hour < 22) return HEADER_BACKGROUNDS.sunset;
  return HEADER_BACKGROUNDS.night;
};

function App() {
  const [activities,    setActivities]    = useState([]);
  const [healthData,    setHealthData]    = useState(null);
  const [stats,         setStats]         = useState(null);
  const [fitnessData,   setFitnessData]   = useState(null);
  const [goalsData,     setGoalsData]     = useState(null);
  const [perfData,      setPerfData]      = useState(null);
  const [recoveryData,  setRecoveryData]  = useState(null);
  const [annualData,    setAnnualData]    = useState(null);
  const [historyData,   setHistoryData]   = useState([]);
  const [customEvents,  setCustomEvents]  = useState([]);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [aiSuggestedInput, setAiSuggestedInput] = useState('');
  const [plannedWorkouts, setPlannedWorkouts] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isStravaAuthenticated, setIsStravaAuthenticated] = useState(false);
  const [isWithingsAuthenticated, setIsWithingsAuthenticated] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [showFitUploadModal, setShowFitUploadModal] = useState(false);
  const [showDailyHealthModal, setShowDailyHealthModal] = useState(false);
  const [isGoalCreatorOpen, setIsGoalCreatorOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [connectionParam, setConnectionParam] = useState(null);
  const [activeTab, setActiveTab] = useState('rendimiento');
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const [morningBriefing, setMorningBriefing] = useState(null);
  const [briefingPhase, setBriefingPhase] = useState('pre_workout');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [dashboardMode, setDashboardMode] = useState(() => {
    try {
      return localStorage.getItem('dashboard_mode') || 'essential';
    } catch {
      return 'essential';
    }
  });

  const handleToggleDashboardMode = (mode) => {
    setDashboardMode(mode);
    try {
      localStorage.setItem('dashboard_mode', mode);
    } catch {}
  };
  
  // COACH STATE LIFTING
  const [coachClients, setCoachClients] = useState([]);
  const [coachClientsLoading, setCoachClientsLoading] = useState(true);

  const fetchCoachClients = useCallback(async () => {
    try {
      setCoachClientsLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      const res = await fetch(`${API_URL}/coach/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCoachClients(data);
      }
    } catch (e) {
      console.error('Error fetching clients', e);
    } finally {
      setCoachClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('hasSeenAiElite')) {
      const timer = setTimeout(() => setShowWhatsNewModal(true), 2500);
      return () => clearTimeout(timer);
    }
  }, []);
  useEffect(() => {
    const handleOpenPricing = () => setIsPricingModalOpen(true);
    window.addEventListener('open-pricing-modal', handleOpenPricing);
    return () => window.removeEventListener('open-pricing-modal', handleOpenPricing);
  }, []);

  const [appMode, setAppMode] = useState(localStorage.getItem('appMode') || 'athlete');

  useEffect(() => {
    if (appMode === 'coach') {
      fetchCoachClients();
    }
  }, [appMode, fetchCoachClients]);

  useEffect(() => {
    if (appMode !== 'athlete') {
      document.documentElement.style.setProperty('--soma-accent', '#FC4C02');
      document.documentElement.style.setProperty('--soma-accent-glow', 'rgba(252, 76, 2, 0.3)');
      document.documentElement.style.setProperty('--health-cyan', '#FC4C02');
      document.documentElement.style.setProperty('--health-cyan-glow', 'rgba(252, 76, 2, 0.3)');
      return;
    }

    let accent = '#FC4C02';
    let accentRgb = '252, 76, 2';
    let glow = 'rgba(252, 76, 2, 0.3)';

    if (activeTab === 'salud') {
      accent = '#00e5ff';
      accentRgb = '0, 229, 255';
      glow = 'rgba(0, 229, 255, 0.3)';
    } else if (activeTab === 'objetivos') {
      accent = '#8b5cf6';
      accentRgb = '139, 92, 246';
      glow = 'rgba(139, 92, 246, 0.3)';
    }

    document.documentElement.style.setProperty('--soma-accent', accent);
    document.documentElement.style.setProperty('--soma-accent-glow', glow);
    document.documentElement.style.setProperty('--section-color', accent);
    document.documentElement.style.setProperty('--section-color-rgb', accentRgb);
    document.documentElement.style.setProperty('--health-cyan', accent);
    document.documentElement.style.setProperty('--health-cyan-glow', glow);
  }, [activeTab, appMode]);
  
  // Reset scroll to top on tab change or login
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab, isAuthenticated, appMode]);

  const [errorMsg,      setErrorMsg]      = useState(null);
  const [successMsg,    setSuccessMsg]    = useState(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  
  // AI Analysis State
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [selectedAthleteObj, setSelectedAthleteObj] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [athletePreferences, setAthletePreferences] = useState(null);
  
  // Messaging state
  const [editingGoal, setEditingGoal] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadBroadcastCount, setUnreadBroadcastCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isMessagingOpen, setIsMessagingOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [runCoachTour, setRunCoachTour] = useState(false);



  // Outdoor mode toggle
  const [isOutdoorMode, setIsOutdoorMode] = useState(localStorage.getItem('outdoorMode') === 'true');

  useEffect(() => {
    if (isOutdoorMode) {
      document.body.classList.add('outdoor-mode');
      localStorage.setItem('outdoorMode', 'true');
    } else {
      document.body.classList.remove('outdoor-mode');
      localStorage.setItem('outdoorMode', 'false');
    }
  }, [isOutdoorMode]);

  const [headerBg, setHeaderBg] = useState('');

  useEffect(() => {
    const currentHour = new Date().getHours();
    setHeaderBg(getBackgroundForHour(currentHour));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connection = params.get('connection');
    if (connection) {
      setConnectionParam(connection);
      // Small timeout to allow React to mount the main view before alerting
      setTimeout(() => {
        if (localStorage.getItem('hasSeenTour')) {
          if (connection.includes('success')) {
            setSuccessMsg('Conexión completada con éxito. Tus datos empezarán a sincronizarse en segundo plano.');
            setTimeout(() => setSuccessMsg(null), 5000);
          }
        }
      }, 500);

      const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
      window.history.replaceState({path: newUrl}, '', newUrl);
    }
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await apiFetch(`/messages/unread/count`);
      if (res) {
        if (res.chatCount !== undefined) setUnreadChatCount(res.chatCount);
        if (res.broadcastCount !== undefined) setUnreadBroadcastCount(res.broadcastCount);
      }
    } catch (e) {
      console.warn('Failed to fetch unread count');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const defaultModules = [
    'lastActivity', 'fitnessChart', 'aerobicEfficiency', 'performanceEvolution', 'sportRadar', 'intensityChart',
    'aiReportCard', 'trainingReadiness', 'recoveryPanel', 'healthSidebar', 'trainingCalendar',
    'recentActivities'
  ];


  useEffect(() => {
    if (appMode === 'athlete' && userProfile && !localStorage.getItem('hasSeenTour')) {
      // Small delay to let the UI render
      const timer = setTimeout(() => setRunTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [appMode, userProfile]);

  useEffect(() => {
    const handleForceSync = () => {
      // Usamos un ligero delay para evitar múltiples llamadas
      setTimeout(() => {
        const btn = document.getElementById('hidden-force-sync-btn');
        if (btn) btn.click();
      }, 50);
    };
    window.addEventListener('force-sync-activities', handleForceSync);
    return () => window.removeEventListener('force-sync-activities', handleForceSync);
  }, []);
  const [visibleModules, setVisibleModules] = useState(() => {
    const saved = localStorage.getItem('visibleModules');
    try {
      const parsed = saved && saved !== 'undefined' ? JSON.parse(saved) : defaultModules;
      const current = Array.isArray(parsed) ? parsed : defaultModules;
      if (!current.includes('recentActivities')) {
        current.push('recentActivities');
      }
      return current;
    } catch {
      return defaultModules;
    }
  });

  const handleOpenNotifications = async () => {
    setIsNotificationsOpen(true);
    try {
      const history = await apiFetch(`/messages`);
      setMessages(history);
    } catch (e) {
      console.error('Error fetching messages', e);
    }
  };

  const handleOpenMessaging = async () => {
    setIsMessagingOpen(true);
    if (appMode === 'coach') {
      if (selectedAthleteId) {
        try {
          const history = await apiFetch(`/messages/${selectedAthleteId}`);
          setMessages(history);
        } catch (e) {
          console.error('Error fetching messages', e);
        }
      }
    } else {
      try {
        // Fetch all messages for the athlete (includes coach and global admin messages)
        const history = await apiFetch(`/messages`);
        setMessages(history);
      } catch (e) {
        console.error('Error fetching messages', e);
      }
    }
  };

  // Cargar mensajes automáticamente cuando el coach selecciona un atleta
  useEffect(() => {
    if (appMode === 'coach' && selectedAthleteId) {
      if (selectedAthleteId.startsWith('demo-')) {
        setMessages([
          { id: 'm1', senderId: selectedAthleteId, receiverId: userProfile?.id, content: 'Hola míster, hoy me he levantado con las piernas un poco pesadas.', createdAt: new Date(Date.now() - 3600000).toISOString(), isRead: true }
        ]);
      } else {
        apiFetch(`/messages/${selectedAthleteId}`)
          .then(history => setMessages(history))
          .catch(e => console.error('Error loading coach messages:', e));
      }
    }
  }, [selectedAthleteId, appMode, userProfile?.id]);

  useEffect(() => {
    let interval;
    // Poll si: (a) el panel de atleta está abierto, o (b) el coach tiene un atleta seleccionado (chat inline)
    const shouldPoll = isMessagingOpen || (appMode === 'coach' && selectedAthleteId);

    if (shouldPoll) {
      interval = setInterval(async () => {
        try {
          if (appMode === 'coach') {
            if (selectedAthleteId) {
              const history = await apiFetch(`/messages/${selectedAthleteId}`);
              setMessages(history);
            }
          } else {
            const history = await apiFetch(`/messages`);
            setMessages(history);
          }
        } catch (e) {
          console.error('Error polling messages', e);
        }
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMessagingOpen, appMode, selectedAthleteId, userProfile]);

  const handleSendMessage = async (targetId, content) => {
    if (targetId.startsWith('demo-')) {
      setMessages(prev => [
        ...prev,
        { id: `m-${Date.now()}`, senderId: userProfile?.id, receiverId: targetId, content, createdAt: new Date().toISOString(), isRead: true }
      ]);
      return;
    }

    const res = await fetch(`${API_URL}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ receiverId: targetId, content })
    });
    if (!res.ok) throw new Error('Error sending message');
    // Refrescar el hilo completo desde el servidor
    try {
      if (appMode === 'coach') {
        const history = await apiFetch(`/messages/${targetId}`);
        setMessages(history);
      } else {
        const history = await apiFetch(`/messages`);
        setMessages(history);
      }
    } catch (e) {
      console.error('Error refreshing messages after send', e);
    }
  };

  const handleMarkMessagesRead = async (targetId) => {
    try {
      if (appMode === 'coach' && targetId) {
        await fetch(`${API_URL}/messages/${targetId}/read`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
      } else {
        await fetch(`${API_URL}/messages/read-all`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
      }
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleAiAnalyze = async () => {
    if (!selectedAthleteObj) return;
    
    setIsAiModalOpen(true);
    setIsAiAnalyzing(true);
    setAiData(null);
    
    try {
      const response = await fetch(`${API_URL}/coach/ai-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          athleteData: selectedAthleteObj,
          recentActivities: activities,
          plannedWorkouts: plannedWorkouts,
          recoveryData: recoveryData
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to analyze');
      setAiData(data);
    } catch (error) {
      console.error('Error in AI Analysis:', error);
      alert(error.message || 'Hubo un error procesando el análisis. Inténtalo de nuevo.');
    } finally {
      setIsAiAnalyzing(false);
    }
  };



  const fetchAll = async (athleteId = null) => {
    setLoading(true);
    try {
      const profile = await fetchSafe('/auth/profile', null);
      if (!profile) { setIsAuthenticated(false); setLoading(false); return; }
      setUserProfile(profile);
      setIsAuthenticated(true);
      if (profile?.id) {
        initPurchases(profile.id).catch(err => console.warn('[RevenueCat] Init purchases warning:', err));
      }

      let localHealthMetrics = [];
      let profilePrefs = profile?.preferences;
      if (typeof profilePrefs === 'string') {
        try { profilePrefs = JSON.parse(profilePrefs); } catch (e) { profilePrefs = {}; }
      }
      if (Capacitor.isNativePlatform() && profilePrefs?.appleHealthConnected) {
        // Sync bloqueando la carga inicial para garantizar que los datos de hoy estén listos
        localHealthMetrics = await syncHealthData(API_URL, localStorage.getItem('token')).catch(e => { console.warn('Sync err:', e); return []; });
      }

      // --- DEMO ATHLETE INTERCEPTION ---
      if (athleteId && athleteId.startsWith('demo-')) {
        // Mock data for the demo athletes
        const rhr = athleteId === 'demo-1' ? 62 : athleteId === 'demo-2' ? 55 : 48;
        const hrv = athleteId === 'demo-1' ? 30 : athleteId === 'demo-2' ? 50 : 80;
        const readScore = athleteId === 'demo-1' ? 35 : athleteId === 'demo-2' ? 65 : 92;
        
        setIsWithingsAuthenticated(true);
        setIsStravaAuthenticated(true);
        setActivities([]);
        setHealthData(null);
        setStats(null);
        setFitnessData([]);
        setGoalsData(null);
        setPerfData(null);
        setRecoveryData({
          today: { readinessScore: readScore, restingHeartRate: rhr, hrv: hrv },
          trends: { rhrAvg: 50, hrvAvg: 60, status: athleteId === 'demo-1' ? 'Fatiga' : 'Óptima' }
        });
        setAnnualData(null);
        setHistoryData([]);
        setPlannedWorkouts([]);
        setLoading(false);
        return;
      }
      // ---------------------------------

      const qs = athleteId ? `?userId=${athleteId}` : '';
      const qsAnd = athleteId ? `&userId=${athleteId}` : '';

      // Lanzar todas las peticiones en paralelo de forma segura con fallbacks
      const [connectionsStatus, acts, health, statsRes, fitness, goals, perf, recovery, annual, history, prefs, planned] = await Promise.all([
        fetchSafe(`/auth/connections/status${qs}`, { withings: false, strava: false }),
        fetchSafe(`/activities${qs}`, []),
        fetchSafe(`/health${qs}`, null),
        fetchSafe(`/stats${qs}`, null),
        fetchSafe(`/fitness${qs}`, []),
        fetchSafe(`/goals/progress${qs}`, []),
        fetchSafe(`/performance${qs}`, null),
        fetchSafe(`/recovery${qs}`, null),
        Promise.resolve(null),
        fetchSafe(`/history${qs}`, []),
        fetchSafe(`/user/preferences${qs}`, {}),
        fetchSafe(`/workouts/planned${qs}`, [])
      ]);

      // Parse prefs if it comes back as a string from the backend
      let parsedPrefs = prefs;
      if (typeof prefs === 'string') {
        try {
          parsedPrefs = JSON.parse(prefs);
        } catch (e) {
          parsedPrefs = {};
        }
      }

      setAthletePreferences(parsedPrefs);
      setIsWithingsAuthenticated(connectionsStatus.withings);
      setIsStravaAuthenticated(connectionsStatus.strava);
      setActivities(Array.isArray(acts) ? acts.filter(a => a && a.start_date) : []);

      // --- DATOS REALES CON FALLBACKS AL PERFIL FISIOLÓGICO DEL ATLETA ---
      const athleteBiometrics = parsedPrefs?.physiology?.biometrics || (!selectedAthleteId ? profile?.preferences?.physiology?.biometrics : null) || {};
      let athleteWeight = athleteBiometrics.weight || parsedPrefs?.weight || (!selectedAthleteId ? profile?.preferences?.weight : null) || null;
      let athleteFat = athleteBiometrics.bodyFat || (!selectedAthleteId ? profile?.preferences?.bodyFat : null) || null;
      let athleteVo2 = athleteBiometrics.vo2max || parsedPrefs?.physiology?.vo2max || (!selectedAthleteId ? profile?.preferences?.physiology?.vo2max : null) || null;
      const athleteSys = athleteBiometrics.systolic || null;
      const athleteDia = athleteBiometrics.diastolic || null;

      const medCtx = parsedPrefs?.medicalContext || (!selectedAthleteId ? profile?.preferences?.medicalContext : '') || '';
      if (!athleteWeight && medCtx) {
        const wMatch = String(medCtx).match(/(?:peso|weight)(?:\s+corporal)?\s*[:=]?\s*(\d+(?:[.,]\d+)?)\s*kg/i);
        if (wMatch) athleteWeight = parseFloat(wMatch[1].replace(',', '.'));
      }
      if (!athleteFat && medCtx) {
        const fMatch = String(medCtx).match(/(?:grasa|fat|índice de grasa)\s*[:=]?\s*(\d+(?:[.,]\d+)?)\s*%/i);
        if (fMatch) athleteFat = parseFloat(fMatch[1].replace(',', '.'));
      }
      if (!athleteVo2 && medCtx) {
        const vo2Match = String(medCtx).match(/vo2\s*(?:max|máx)?\s*[:=]?\s*(\d+(?:[.,]\d+)?)/i);
        if (vo2Match) athleteVo2 = parseFloat(vo2Match[1].replace(',', '.'));
      }

      const enrichedHealth = health ? { ...health } : {};
      const todayStr = new Date().toISOString().split('T')[0];
      
      if (!enrichedHealth.weight) enrichedHealth.weight = { history: [] };
      if (enrichedHealth.weight.current == null && athleteWeight != null) enrichedHealth.weight.current = athleteWeight;
      if (enrichedHealth.weight.previous == null) enrichedHealth.weight.previous = enrichedHealth.weight.current;
      if (!Array.isArray(enrichedHealth.weight.history)) enrichedHealth.weight.history = [];
      if (enrichedHealth.weight.history.length === 0 && enrichedHealth.weight.current != null) {
        enrichedHealth.weight.history = [{ date: todayStr, value: enrichedHealth.weight.current }];
      }

      if (!enrichedHealth.bodyFat) enrichedHealth.bodyFat = { history: [] };
      if (enrichedHealth.bodyFat.current == null && athleteFat != null) enrichedHealth.bodyFat.current = athleteFat;
      if (enrichedHealth.bodyFat.previous == null) enrichedHealth.bodyFat.previous = enrichedHealth.bodyFat.current;
      if (!Array.isArray(enrichedHealth.bodyFat.history)) enrichedHealth.bodyFat.history = [];
      if (enrichedHealth.bodyFat.history.length === 0 && enrichedHealth.bodyFat.current != null) {
        enrichedHealth.bodyFat.history = [{ date: todayStr, value: enrichedHealth.bodyFat.current }];
      }

      const curW = enrichedHealth.weight.current;
      if (!enrichedHealth.composition) enrichedHealth.composition = {};
      if (enrichedHealth.composition.muscleMass == null && curW != null) {
        enrichedHealth.composition.muscleMass = parseFloat((curW * 0.8).toFixed(1));
      }
      if (enrichedHealth.composition.boneMass == null && curW != null) {
        enrichedHealth.composition.boneMass = parseFloat((curW * 0.04).toFixed(1));
      }

      if (!enrichedHealth.cardio) enrichedHealth.cardio = { pwv: null, vascularAge: null };
      if (enrichedHealth.cardio.vo2max == null && athleteVo2 != null) enrichedHealth.cardio.vo2max = athleteVo2;

      if (!enrichedHealth.bloodPressure) enrichedHealth.bloodPressure = { systolic: {}, diastolic: {} };
      if (!enrichedHealth.bloodPressure.systolic) enrichedHealth.bloodPressure.systolic = {};
      if (!enrichedHealth.bloodPressure.diastolic) enrichedHealth.bloodPressure.diastolic = {};
      if (enrichedHealth.bloodPressure.systolic.current == null && athleteSys != null) {
        enrichedHealth.bloodPressure.systolic.current = athleteSys;
        enrichedHealth.bloodPressure.systolic.previous = athleteSys;
        enrichedHealth.bloodPressure.systolic.history = [athleteSys];
      }
      if (enrichedHealth.bloodPressure.diastolic.current == null && athleteDia != null) {
        enrichedHealth.bloodPressure.diastolic.current = athleteDia;
        enrichedHealth.bloodPressure.diastolic.previous = athleteDia;
        enrichedHealth.bloodPressure.diastolic.history = [athleteDia];
      }

      if (!enrichedHealth.spo2) enrichedHealth.spo2 = { current: null, history: [] };
      
      const enrichedRecovery = recovery ? { ...recovery } : {};
      
      if (enrichedHealth.spo2.current == null && enrichedRecovery.spo2Data?.current != null) {
        enrichedHealth.spo2.current = enrichedRecovery.spo2Data.current;
      }
      
      // FUSIÓN LOCAL DE DATOS DE SALUD (BYPASS DB VPS)
      if (localHealthMetrics && localHealthMetrics.length > 0) {
        // Ordenar cronológicamente para que el último elemento sea SIEMPRE el día de hoy o el más reciente
        localHealthMetrics.sort((a, b) => a.date.localeCompare(b.date));
        const latestLocal = localHealthMetrics[localHealthMetrics.length - 1];
        
        const findLatest = (key) => {
          for (let i = localHealthMetrics.length - 1; i >= 0; i--) {
            if (localHealthMetrics[i][key] != null) return localHealthMetrics[i][key];
          }
          return null;
        };

        if (!enrichedRecovery.temperature) enrichedRecovery.temperature = {};
        const latestTemp = findLatest('temperature');
        if (latestTemp != null) enrichedRecovery.temperature.current = latestTemp;
        
        if (!enrichedRecovery.sleepData) enrichedRecovery.sleepData = {};
        const latestSleepEff = findLatest('sleepEfficiency');
        if (latestSleepEff != null) enrichedRecovery.sleepData.efficiency = latestSleepEff;
        
        const latestSleepDur = findLatest('sleepDuration');
        if (latestSleepDur != null) {
          enrichedRecovery.sleepData.duration = latestSleepDur;
        }

        const latestDeep = findLatest('deepSleepDuration');
        const latestLight = findLatest('lightSleepDuration');
        const latestRem = findLatest('remSleepDuration');
        const latestAwake = findLatest('sleepAwakeDuration');
        if (latestDeep != null || latestLight != null || latestRem != null) {
          enrichedRecovery.sleepData.stages = {
            deep: latestDeep != null ? parseFloat(Number(latestDeep).toFixed(1)) : 0,
            light: latestLight != null ? parseFloat(Number(latestLight).toFixed(1)) : 0,
            rem: latestRem != null ? parseFloat(Number(latestRem).toFixed(1)) : 0,
            awake: latestAwake != null ? parseFloat(Number(latestAwake).toFixed(1)) : 0
          };
        }
        
        if (!enrichedRecovery.rhrData) enrichedRecovery.rhrData = {};
        const latestRhr = findLatest('rhr');
        if (latestRhr != null) enrichedRecovery.rhrData.current = Math.round(latestRhr);

        if (!enrichedRecovery.hrvData) enrichedRecovery.hrvData = {};
        const latestHrv = findLatest('hrv');
        if (latestHrv != null) enrichedRecovery.hrvData.current = Math.round(latestHrv);
        
        if (!enrichedHealth.metabolism) enrichedHealth.metabolism = { glucose: {} };
        if (!enrichedHealth.metabolism.glucose) enrichedHealth.metabolism.glucose = {};
        const latestGlucose = findLatest('glucoseCgm');
        if (latestGlucose != null) enrichedHealth.metabolism.glucose.current = Math.round(latestGlucose);
        
        const latestTimeInRange = findLatest('timeInRange');
        if (latestTimeInRange != null) enrichedHealth.metabolism.timeInRange = { current: latestTimeInRange };

        if (!enrichedHealth.biomechanics) enrichedHealth.biomechanics = {};
        if (!enrichedHealth.biomechanics.asymmetry) enrichedHealth.biomechanics.asymmetry = {};
        const latestAsym = findLatest('gaitAsymmetry');
        if (latestAsym != null) enrichedHealth.biomechanics.asymmetry.current = latestAsym;
        
        const latestGct = findLatest('groundContactTime');
        if (latestGct != null) enrichedHealth.biomechanics.groundContactTime = { current: latestGct };
        const latestVo = findLatest('verticalOscillation');
        if (latestVo != null) enrichedHealth.biomechanics.verticalOscillation = { current: latestVo };

        if (!enrichedHealth.weight) enrichedHealth.weight = { history: [] };
        const weightHistoryArr = localHealthMetrics.filter(m => m.weight != null).map(m => ({ date: m.date, value: Math.round(m.weight * 10) / 10 }));
        if (weightHistoryArr.length > 0) {
          enrichedHealth.weight.history = weightHistoryArr;
          enrichedHealth.weight.current = weightHistoryArr[weightHistoryArr.length - 1].value;
          if (weightHistoryArr.length > 1) {
            enrichedHealth.weight.previous = weightHistoryArr[weightHistoryArr.length - 2].value;
          }
        }
        
        const latestWeight = findLatest('weight');
        if (latestWeight != null) {
          enrichedHealth.weight.current = Math.round(latestWeight * 10) / 10;
          enrichedHealth.composition = {
            muscleMass: parseFloat((enrichedHealth.weight.current * 0.8).toFixed(1)),
            boneMass: parseFloat((enrichedHealth.weight.current * 0.04).toFixed(1)),
            waterPct: enrichedHealth.composition?.waterPct || null
          };
        }

        if (!enrichedHealth.bodyFat) enrichedHealth.bodyFat = {};
        const fatHistoryArr = localHealthMetrics.filter(m => m.bodyFat != null).map(m => ({ date: m.date, value: Math.round(m.bodyFat * 10) / 10 }));
        if (fatHistoryArr.length > 0) {
          enrichedHealth.bodyFat.history = fatHistoryArr;
          enrichedHealth.bodyFat.current = fatHistoryArr[fatHistoryArr.length - 1].value;
          if (fatHistoryArr.length > 1) {
            enrichedHealth.bodyFat.previous = fatHistoryArr[fatHistoryArr.length - 2].value;
          }
        }

        const latestBodyFat = findLatest('bodyFat');
        if (latestBodyFat != null) {
          enrichedHealth.bodyFat.current = Math.round(latestBodyFat * 10) / 10;
        }

        if (!enrichedHealth.spo2) enrichedHealth.spo2 = {};
        const latestSpo2 = findLatest('spO2');
        if (latestSpo2 != null) {
          enrichedHealth.spo2.current = Math.round(latestSpo2);
          if (!enrichedRecovery.spo2Data) enrichedRecovery.spo2Data = {};
          enrichedRecovery.spo2Data.current = Math.round(latestSpo2);
        }

        if (!enrichedHealth.cardio) enrichedHealth.cardio = {};
        const latestVo2 = findLatest('vo2Max');
        if (latestVo2 != null) enrichedHealth.cardio.vo2max = Math.round(latestVo2 * 10) / 10;

        if (!enrichedHealth.activity) enrichedHealth.activity = { history: [] };
        const latestSteps = findLatest('steps');
        if (latestSteps != null) enrichedHealth.activity.currentSteps = latestSteps;
        
        const localStepsHistory = localHealthMetrics.filter(m => m.steps != null).map(m => m.steps);
        if (localStepsHistory.length > 0) {
          enrichedHealth.activity.history = localStepsHistory;
          if (localStepsHistory.length > 1) {
            enrichedHealth.activity.previousSteps = localStepsHistory[localStepsHistory.length - 2];
          }
        }
        
        const latestCalories = findLatest('activeCalories');
        if (latestCalories != null) enrichedHealth.activity.activeCalories = latestCalories;

        const latestResp = findLatest('respiratoryRate');
        if (latestResp != null) {
          if (!enrichedRecovery.sleepData) enrichedRecovery.sleepData = {};
          if (!enrichedRecovery.sleepData.breathingRate) enrichedRecovery.sleepData.breathingRate = {};
          enrichedRecovery.sleepData.breathingRate.current = Math.round(latestResp * 10) / 10;
        }

        if (!enrichedHealth.bloodPressure) enrichedHealth.bloodPressure = { systolic: {}, diastolic: {} };
        if (!enrichedHealth.bloodPressure.systolic) enrichedHealth.bloodPressure.systolic = {};
        if (!enrichedHealth.bloodPressure.diastolic) enrichedHealth.bloodPressure.diastolic = {};
        const latestSys = findLatest('systolic');
        if (latestSys != null) enrichedHealth.bloodPressure.systolic.current = Math.round(latestSys);
        const latestDia = findLatest('diastolic');
        if (latestDia != null) enrichedHealth.bloodPressure.diastolic.current = Math.round(latestDia);
      }

      setHealthData(enrichedHealth);
      setStats(statsRes);
      const cleanFitness = Array.isArray(fitness) ? fitness.filter(f => f) : [];
      setFitnessData(cleanFitness);
      
      // Si hoy hay actividades en acts pero el registro de fitness de hoy tiene carga 0 (o no está),
      // refrescamos en background /fitness para evitar desfases de carrera de sincronización
      const hasTodayAct = Array.isArray(acts) && acts.some(a => a && a.start_date && a.start_date.startsWith(todayStr));
      const todayFitness = cleanFitness.find(f => f && f.date === todayStr);
      if (hasTodayAct && (!todayFitness || Number(todayFitness.load || 0) === 0)) {
        fetchSafe(`/fitness${qs}`, []).then(freshFitness => {
          if (Array.isArray(freshFitness) && freshFitness.length > 0) {
            setFitnessData(freshFitness.filter(f => f));
          }
        }).catch(err => console.warn('[App] Error al revalidar fitness de hoy:', err));
      }

      setGoalsData(goals);
      setPerfData(perf);
      setRecoveryData(enrichedRecovery);
      // ----------------------------------------
      setAnnualData(annual);
      setHistoryData(Array.isArray(history) ? history.filter(h => h) : []);
      setPlannedWorkouts(Array.isArray(planned) ? planned : []);

      if (!athleteId) {
        if (parsedPrefs && parsedPrefs.visible_modules) {
          setVisibleModules(Array.isArray(parsedPrefs.visible_modules) ? parsedPrefs.visible_modules : defaultModules);
        }
        if (profile?.role === 'admin') {
          setAppMode('admin');
        } else if (profile?.role === 'trainer') {
          setAppMode('coach');
        } else if (parsedPrefs && parsedPrefs.app_mode) {
          setAppMode(parsedPrefs.app_mode);
        }
        if (parsedPrefs && parsedPrefs.custom_events) {
          setCustomEvents(Array.isArray(parsedPrefs.custom_events) ? parsedPrefs.custom_events : []);
        }
        if (parsedPrefs && parsedPrefs.onboardingComplete) {
          localStorage.setItem('onboardingComplete', 'true');
        }
      }

      const effectiveAppMode = !athleteId ? (parsedPrefs?.app_mode || appMode) : appMode;
      if (effectiveAppMode !== 'coach' && !athleteId) {
        const scoreForBriefing = (enrichedRecovery && enrichedRecovery.readinessScore !== undefined)
          ? enrichedRecovery.readinessScore
          : (recovery && recovery.readinessScore !== undefined ? recovery.readinessScore : 50);

        setBriefingLoading(true);
        const url = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
        const isRecMode = (parsedPrefs?.recoveryMode || prefs?.recoveryMode) ? 'true' : 'false';
        fetch(`${url}/ai/morning-briefing?readinessScore=${scoreForBriefing}&recoveryMode=${isRecMode}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
        .then(res => res.json())
        .then(resData => {
           if (resData.briefing) {
             setMorningBriefing(resData.briefing);
             if (resData.phase) setBriefingPhase(resData.phase);
           }
        })
        .catch(e => console.error("Error prefetching briefing:", e))
        .finally(() => setBriefingLoading(false));
      }

    } catch (error) {
      if (error.message === 'NO_AUTH') { 
        setIsAuthenticated(false); 
        localStorage.removeItem('onboardingComplete');
      }
      else { setErrorMsg('Error de conexión o autenticación: ' + error.message); }
    } finally { setLoading(false); }
  };

  const savePreferences = async (newVisibleModules, newAppMode) => {
    try {
      await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ visibleModules: newVisibleModules, appMode: newAppMode })
      });
    } catch (e) {
      console.error('Error saving preferences', e);
    }
  };

  useEffect(() => { fetchAll(selectedAthleteId); }, [selectedAthleteId]);

  useEffect(() => {
    let appStateListener = null;
    let appUrlListener = null;

    if (Capacitor.isNativePlatform()) {
      const setupListeners = async () => {
        appStateListener = await CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
          if (isActive && isAuthenticated && userProfile?.preferences?.appleHealthConnected) {
            syncHealthData(API_URL, localStorage.getItem('token'))
              .then(() => {
                console.log('[AppleHealth] Sync completed, refreshing dashboard...');
                fetchAll(selectedAthleteId);
              })
              .catch(e => console.warn('Background sync err:', e));
          }
        });

        appUrlListener = await CapacitorApp.addListener('appUrlOpen', async (event) => {
          console.log('[CapacitorApp] Deep link received:', event.url);
          try {
            await Browser.close();
          } catch {
            // Ignorar si el browser ya estaba cerrado
          }

          try {
            // ej: "nousmetrics://strava-callback?connection=strava_success"
            const urlObj = new URL(event.url);
            const connection = urlObj.searchParams.get('connection');
            if (connection) {
              setConnectionParam(connection);
              if (connection.includes('success')) {
                setSuccessMsg('Conexión completada con éxito. Tus datos se están sincronizando en segundo plano.');
                setTimeout(() => setSuccessMsg(null), 5000);
                const profile = await fetchSafe('/auth/profile', null);
                if (profile) {
                  setUserProfile(profile);
                  setIsStravaAuthenticated(Boolean(profile.stravaConnected));
                  setIsWithingsAuthenticated(Boolean(profile.withingsConnected));
                }
                fetchAll(selectedAthleteId);
              } else if (connection.includes('error')) {
                setErrorMsg('Error durante la vinculación del servicio. Inténtalo de nuevo.');
                setTimeout(() => setErrorMsg(null), 5000);
              }
            }
          } catch (err) {
            console.warn('[DeepLink] Error procesando appUrlOpen:', err);
          }
        });
      };
      setupListeners();
    }

    return () => {
      if (appStateListener) appStateListener.remove();
      if (appUrlListener) appUrlListener.remove();
    };
  }, [isAuthenticated, userProfile, selectedAthleteId]);

  const handleLoginSuccess = async (token, user) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    try {
        const profile = await apiFetch('/auth/profile');
        if (profile.role === 'admin') setAppMode('admin');
        else setAppMode(profile.role === 'trainer' ? 'coach' : 'athlete');
        const prefs = typeof profile?.preferences === 'string' ? JSON.parse(profile.preferences) : profile?.preferences;
        if (prefs?.onboardingComplete || profile?.email === 'apple.review@nousmetrics.com') {
          localStorage.setItem('onboardingComplete', 'true');
        }
    } catch (e) {
        console.error('Error fetching profile on login', e);
    }
    fetchAll();
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (e) {
      console.warn('Logout API err:', e);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('onboardingComplete');
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  useEffect(() => {
    localStorage.setItem('visibleModules', JSON.stringify(visibleModules));
  }, [visibleModules]);

  useEffect(() => {
    localStorage.setItem('appMode', appMode);
  }, [appMode]);

  const handleDeleteGoal = async (id) => {
    if (!window.confirm('¿Seguro que quieres eliminar este objetivo?')) return;
    try {
      const qs = selectedAthleteId ? `?userId=${selectedAthleteId}` : '';
      await fetch(`${API_URL}/goals/${id}${qs}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const fresh = await apiFetch(`/goals/progress${qs}`);
      setGoalsData(fresh);
    } catch (e) { console.error(e); }
  };

  const handleGoalCreated = async () => {
    const qs = selectedAthleteId ? `?userId=${selectedAthleteId}` : '';
    const fresh = await apiFetch(`/goals/progress${qs}`);
    setGoalsData(fresh);
    setEditingGoal(null);
  };

  const handleSyncFullHistory = async () => {
    setLoading(true);
    const qs = selectedAthleteId ? `&userId=${selectedAthleteId}` : '';
    const qsOnly = selectedAthleteId ? `?userId=${selectedAthleteId}` : '';
    try {
      const freshHistory = await apiFetch(`/history?sync=full${qs}`);
      const forceQs = selectedAthleteId ? `?force=true&userId=${selectedAthleteId}` : '?force=true';
      const freshActs = await apiFetch(`/activities${forceQs}`);
      setActivities(Array.isArray(freshActs) ? freshActs : []);
      const freshFitness = await apiFetch(`/fitness${qsOnly}`);
      setFitnessData(Array.isArray(freshFitness) ? freshFitness.filter(f => f) : []);
      const freshStats = await apiFetch(`/stats${qsOnly}`);
      setStats(freshStats);
      const freshHealth = await apiFetch(`/health?force=true${qs}`);
      setHealthData(freshHealth);
      const freshPlanned = await apiFetch(`/workouts/planned${qsOnly}`);
      setPlannedWorkouts(Array.isArray(freshPlanned) ? freshPlanned : []);
    } catch (e) {
      console.error(e);
      setErrorMsg('Error realizando sincronización completa de histórico: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async (activityId, notes) => {
    await fetch(`${API_URL}/activities/${activityId}/notes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ notes })
    });
    // Update local state so it shows up immediately
    setActivities(acts => acts.map(a => a.id === activityId ? { ...a, trainerNotes: notes } : a));
  };

  const handleSavePlanned = async (workout) => {
    const qsOnly = selectedAthleteId ? `?userId=${selectedAthleteId}` : '';
    const res = await fetch(`${API_URL}/workouts/planned${qsOnly}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify(workout)
    });
    if (!res.ok) throw new Error(await res.text());
    const saved = await res.json();
    setPlannedWorkouts(prev => [...prev, saved]);
  };

  const handleDeletePlanned = async (id) => {
    try {
      await fetchSafe(`/workouts/planned/${id}?userId=${selectedAthleteId || userProfile?.id}`, null, { method: 'DELETE' });
      setPlannedWorkouts(prev => prev.filter(w => w.id !== id));
    } catch (e) {
      console.error('Error deleting planned workout', e);
    }
  };

  const handleCompletePlanned = async (id, rpe, athleteNotes) => {
    const res = await fetch(`${API_URL}/workouts/planned/${id}/complete`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ rpe, athleteNotes })
    });
    if (!res.ok) throw new Error(await res.text());
    const updated = await res.json();
    setPlannedWorkouts(prev => prev.map(w => w.id === id ? updated : w));
  };

  // Event Handlers para Próximas Competiciones
  const saveCustomEvents = async (updatedEvents) => {
    try {
      const qs = selectedAthleteId ? `?userId=${selectedAthleteId}` : '';
      await fetch(`${API_URL}/user/preferences${qs}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ custom_events: updatedEvents })
      });
    } catch (e) {
      console.error('Error saving custom events', e);
    }
  };

  const handleAddEvent = (newEvent) => {
    setCustomEvents(prev => {
      const updated = [...prev, newEvent];
      saveCustomEvents(updated);
      return updated;
    });
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      setCustomEvents(prev => {
        const updated = prev.filter(e => e.id !== eventId);
        saveCustomEvents(updated);
        return updated;
      });
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.5rem' }}>
      <span className="text-gradient">Cargando Dashboard...</span>
    </div>
  );

  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (!isAuthenticated) {
    return (
      <div style={{ position: 'relative' }}>
        <button 
          onClick={() => window.location.href = '/'}
          style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 100, background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer', backdropFilter: 'blur(10px)' }}
        >
          ← Volver al inicio
        </button>
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // Si está autenticado en la plataforma pero no tiene Strava conectado, 
  // mostramos un botón para conectar con Strava, a menos que sea coach (que no necesita Strava para ver a sus atletas).
  const isStravaConnected = activities.length > 0 || stats;
  const parsedUserPrefs = typeof userProfile?.preferences === 'string'
    ? (() => { try { return JSON.parse(userProfile.preferences); } catch(e) { return {}; } })()
    : (userProfile?.preferences || {});
  const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true' || 
    parsedUserPrefs?.onboardingComplete === true || 
    (activities && activities.length > 0) ||
    userProfile?.email === 'apple.review@nousmetrics.com';

  const handleOnboardingComplete = async () => {
    localStorage.setItem('onboardingComplete', 'true');
    try {
      await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ onboardingComplete: true })
      });
    } catch(e) {
      console.error('Error saving onboarding complete status', e);
    }
    window.location.reload(); // Simple way to re-render sin hook violation
  };
  
  if (!onboardingComplete && appMode === 'athlete') {
    return (
      <OnboardingWizard 
        onLogout={handleLogout} 
        userId={(() => {
          try {
            const tk = localStorage.getItem('token');
            if (!tk) return '';
            const base64 = tk.split('.')[1];
            return JSON.parse(decodeURIComponent(escape(atob(base64)))).id;
          } catch (e) {
            return '';
          }
        })()}
        userProfile={userProfile}
        connectionParam={connectionParam}
        onComplete={handleOnboardingComplete}
        isWithingsAuthenticated={isWithingsAuthenticated}
      />
    );
  }

  const coachOnboardingComplete = localStorage.getItem('coachOnboardingComplete') === 'true';
  
  const handleCoachOnboardingComplete = () => {
    localStorage.setItem('coachOnboardingComplete', 'true');
    window.location.reload();
  };

  if (!coachOnboardingComplete && appMode === 'coach') {
    return (
      <CoachOnboardingWizard onComplete={handleCoachOnboardingComplete} />
    );
  }

  // Generar saludo dinámico
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getReadinessColor = (score) => {
    if (score >= 90) return '#00e5ff'; // Cyan
    if (score >= 70) return '#10b981'; // Green
    if (score >= 35) return '#f59e0b'; // Naranja
    return '#ef4444'; // Rojo
  };

  const getReadinessLabel = (score) => {
    if (score >= 90) return 'Sobresaliente';
    if (score >= 70) return 'Óptima';
    if (score >= 35) return 'Fatiga';
    return 'Sobrecarga';
  };

  // Cálculo de Notificaciones (Entrenos futuros o Feedback reciente)
  const todayDate = new Date();
  todayDate.setHours(0,0,0,0);
  const sevenDaysAgo = new Date(todayDate);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const hasPendingWorkouts = plannedWorkouts.some(pw => new Date(pw.date) >= todayDate);
  const hasRecentFeedback = activities.some(a => a.trainerNotes && new Date(a.start_date) >= sevenDaysAgo);
  const hasNotifications = hasPendingWorkouts || hasRecentFeedback;

  const getSubGreeting = () => {
    if (appMode === 'admin') return 'Gestión global de usuarios y auditoría de suscripciones';
    if (appMode === 'coach' && selectedAthleteId) return 'Seguimiento y métricas de tu deportista';
    if (appMode === 'coach') return 'Análisis fisiológico y gestión de la carga de entrenamiento';
    return '¿Listo para superar tus límites hoy?';
  };

  const adminToken = localStorage.getItem('adminToken');

  const handleReturnToAdmin = () => {
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('adminToken');
      localStorage.setItem('appMode', 'admin');
      window.location.href = '/app';
    }
  };

  return (
    <div className={`app-container ${appMode === 'athlete' || appMode === 'coach' ? 'dashboard-grid-pro' : ''}`}>
      {(appMode === 'athlete' || appMode === 'coach') && (
        <aside className="dashboard-sidebar-pro" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(10, 15, 30, 0.98) 100%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '4px 0 25px rgba(0,0,0,0.3)',
          padding: '1.25rem 1rem',
          boxSizing: 'border-box'
        }}>
          
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto', paddingRight: '0.25rem' }}>
            
            {/* LOGO & DESKTOP / MOBILE TOP HEADER */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', paddingLeft: '0.25rem' }}>
              <NousLogo size="nav" showSlogan={false} variant={appMode === 'coach' ? "mentor" : "soma"} />
              
              <div className="desktop-hide" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                {/* 1. Chat con Entrenador */}
                <button 
                  onClick={handleOpenMessaging} 
                  style={{ 
                    position: 'relative', 
                    background: 'rgba(255,255,255,0.08)', 
                    color: 'white', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '50%', 
                    width: '38px', 
                    height: '38px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }} 
                  title="Mensajes"
                >
                  <MessageCircle size={18} />
                  {unreadChatCount > 0 && (
                    <span style={{ 
                      position: 'absolute', top: '-4px', right: '-4px', 
                      background: 'var(--health-cyan)', color: '#000', 
                      fontSize: '0.65rem', fontWeight: 'bold', 
                      borderRadius: '50%', width: '18px', height: '18px', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0, 229, 255, 0.4)'
                    }}>
                      {unreadChatCount}
                    </span>
                  )}
                </button>

                {/* 2. Notificaciones */}
                <button 
                  onClick={handleOpenNotifications} 
                  style={{ 
                    position: 'relative', 
                    background: 'rgba(255,255,255,0.08)', 
                    color: 'white', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '50%', 
                    width: '38px', 
                    height: '38px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }} 
                  title="Notificaciones"
                >
                  <Bell size={18} />
                  {unreadBroadcastCount > 0 && (
                    <span style={{ 
                      position: 'absolute', top: '2px', right: '2px', 
                      background: 'var(--health-cyan)', width: '8px', height: '8px', 
                      borderRadius: '50%', border: '2px solid rgba(15,23,42,1)' 
                    }} />
                  )}
                </button>

                {/* 3. Ajustes Personales */}
                <button 
                  onClick={() => setShowSettingsModal(true)} 
                  style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    color: 'white', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '50%', 
                    width: '38px', 
                    height: '38px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }} 
                  title="Ajustes y Cuenta"
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>

            {/* NAVIGATION MENU */}
            <nav className="mobile-hide" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
              {appMode === 'athlete' ? (
                <>
                  {/* CATEGORÍA 1: RENDIMIENTO & ANALÍTICA */}
                  <div>
                    <h4 style={{ fontSize: '0.62rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', fontWeight: 500, letterSpacing: '0.05em', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                      Rendimiento
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <button 
                        onClick={() => setActiveTab('rendimiento')} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', 
                          background: activeTab === 'rendimiento' ? 'rgba(252, 76, 2, 0.1)' : 'transparent', 
                          color: activeTab === 'rendimiento' ? '#FC4C02' : 'rgba(255,255,255,0.65)', 
                          border: 'none', 
                          borderLeft: activeTab === 'rendimiento' ? '3px solid #FC4C02' : '3px solid transparent',
                          borderRadius: '8px', cursor: 'pointer', textAlign: 'left', fontWeight: activeTab === 'rendimiento' ? 600 : 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { if (activeTab !== 'rendimiento') { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}}
                        onMouseLeave={e => { if (activeTab !== 'rendimiento') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}}
                      >
                        <Activity size={16} style={{ flexShrink: 0 }} /> Dashboard General
                      </button>
                    </div>
                  </div>

                  {/* CATEGORÍA 2: SALUD Y PLANIFICACIÓN */}
                  <div>
                    <h4 style={{ fontSize: '0.62rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', fontWeight: 500, letterSpacing: '0.05em', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                      Fisiología y Agenda
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <button 
                        onClick={() => setActiveTab('salud')} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', 
                          background: activeTab === 'salud' ? 'rgba(0, 229, 255, 0.1)' : 'transparent', 
                          color: activeTab === 'salud' ? '#00e5ff' : 'rgba(255,255,255,0.65)', 
                          border: 'none', 
                          borderLeft: activeTab === 'salud' ? '3px solid #00e5ff' : '3px solid transparent',
                          borderRadius: '8px', cursor: 'pointer', textAlign: 'left', fontWeight: activeTab === 'salud' ? 600 : 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { if (activeTab !== 'salud') { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}}
                        onMouseLeave={e => { if (activeTab !== 'salud') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}}
                      >
                        <Heart size={16} style={{ flexShrink: 0 }} /> Salud y Fisiología
                      </button>
                      
                      <button 
                        onClick={() => setActiveTab('objetivos')} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', 
                          background: activeTab === 'objetivos' ? 'rgba(16, 185, 129, 0.1)' : 'transparent', 
                          color: activeTab === 'objetivos' ? '#10b981' : 'rgba(255,255,255,0.65)', 
                          border: 'none', 
                          borderLeft: activeTab === 'objetivos' ? '3px solid #10b981' : '3px solid transparent',
                          borderRadius: '8px', cursor: 'pointer', textAlign: 'left', fontWeight: activeTab === 'objetivos' ? 600 : 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { if (activeTab !== 'objetivos') { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}}
                        onMouseLeave={e => { if (activeTab !== 'objetivos') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}}
                      >
                        <Target size={16} style={{ flexShrink: 0 }} /> Planificación y Metas
                      </button>
                    </div>
                  </div>

                  {/* CATEGORÍA 3: INTELIGENCIA ARTIFICIAL */}
                  <div>
                    <h4 style={{ fontSize: '0.62rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', fontWeight: 500, letterSpacing: '0.05em', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                      Inteligencia Artificial
                    </h4>
                    <button 
                      onClick={() => setIsAiDrawerOpen(true)} 
                      style={{ 
                        width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.7rem', 
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)', 
                        color: '#c4b5fd', border: '1px solid rgba(139, 92, 246, 0.2)', 
                        borderRadius: '8px', cursor: 'pointer', textAlign: 'left', fontWeight: 500, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)'; e.currentTarget.style.borderColor = '#a78bfa'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)'; e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)'; }}
                    >
                      <Sparkles size={16} style={{ color: '#a78bfa', flexShrink: 0 }} /> Coach IA Deportivo
                    </button>
                  </div>

                  {/* CATEGORÍA 4: HERRAMIENTAS Y ACCIONES */}
                  <div>
                    <h4 style={{ fontSize: '0.62rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', fontWeight: 500, letterSpacing: '0.05em', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                      Herramientas
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <button 
                        onClick={handleOpenMessaging} 
                        style={{ 
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.7rem', 
                          background: 'transparent', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}><MessageCircle size={16} style={{ flexShrink: 0 }} /> Mensajes</span>
                        {unreadChatCount > 0 && <span style={{ background: '#00e5ff', color: '#000', fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: '10px' }}>{unreadChatCount}</span>}
                      </button>

                      <button 
                        onClick={handleOpenNotifications} 
                        style={{ 
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.7rem', 
                          background: 'transparent', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}><Bell size={16} style={{ flexShrink: 0 }} /> Notificaciones</span>
                        {unreadBroadcastCount > 0 && <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00e5ff', display: 'inline-block' }} />}
                      </button>

                      <button 
                        onClick={() => fetchAll()} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', 
                          background: 'transparent', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        <RefreshCw size={16} style={{ flexShrink: 0 }} className={loading ? 'animate-spin' : ''} /> Sincronizar
                      </button>

                      <button 
                        onClick={() => setShowSettingsModal(true)} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', 
                          background: 'transparent', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        <Settings size={16} style={{ flexShrink: 0 }} /> Centro de Control
                      </button>

                      <button 
                        onClick={() => setShowFeedbackModal(true)} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', 
                          background: 'transparent', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 400, fontSize: '0.82rem', whiteSpace: 'nowrap', transition: 'all 0.2s' 
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        <MessageSquare size={16} style={{ flexShrink: 0 }} /> Soporte y Feedback
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <button 
                      onClick={() => { setSelectedAthleteId(null); setSelectedAthleteObj(null); }}
                      style={{ 
                        width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', 
                        background: !selectedAthleteId ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.08)', 
                        color: !selectedAthleteId ? '#c4b5fd' : 'rgba(255,255,255,0.7)', 
                        border: 'none', 
                        borderLeft: !selectedAthleteId ? '3px solid #8b5cf6' : '3px solid transparent',
                        borderRadius: '12px', cursor: 'pointer', textAlign: 'left', fontWeight: !selectedAthleteId ? 700 : 500, transition: 'all 0.2s' 
                      }}
                      onMouseEnter={e => { if (selectedAthleteId) { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)'; e.currentTarget.style.color = '#fff'; } }}
                      onMouseLeave={e => { if (selectedAthleteId) { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; } }}
                    >
                      <Activity size={20} /> Command Center
                    </button>
                  </div>
                  
                  {/* Tus Atletas Section */}
                  <div style={{ marginBottom: '1rem' }}>
                    <h3 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '1px', marginBottom: '0.75rem', paddingLeft: '0.75rem' }}>
                      Tus Atletas
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {coachClientsLoading ? (
                        <div style={{ padding: '0.5rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Cargando...</div>
                      ) : coachClients.map(client => {
                        const isAlerta = client.recentStatus === 'Fatiga Alta' || (client.readinessScore && client.readinessScore < 40);
                        const isOptimo = client.recentStatus === 'Óptimo' || (client.readinessScore && client.readinessScore > 75);
                        const dotColor = isAlerta ? '#ef4444' : isOptimo ? '#10b981' : '#f59e0b';
                        const isSelected = selectedAthleteId === client.id;
                        
                        return (
                          <button 
                            key={client.id}
                            onClick={() => {
                              setSelectedAthleteId(client.id);
                              setSelectedAthleteObj(client);
                            }}
                            style={{ 
                              display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.85rem', 
                              background: isSelected ? 'rgba(139, 92, 246, 0.2)' : 'transparent', 
                              color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)', 
                              border: 'none', 
                              borderLeft: isSelected ? '3px solid #8b5cf6' : '3px solid transparent',
                              borderRadius: '8px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                              fontWeight: isSelected ? 600 : 400
                            }}
                            onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#fff'; } }}
                            onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; } }}
                          >
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, boxShadow: `0 0 8px ${dotColor}80` }}></span>
                            <span style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name.split(' ')[0]} {client.name.split(' ')[1]?.[0]}.</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Invitaciones Section */}
                  <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                    <h3 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '1px', marginBottom: '0.75rem', paddingLeft: '0.75rem' }}>
                      Invitaciones
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <button style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                        <Plus size={14} /> Nueva Invitación
                      </button>
                      <div style={{ padding: '0.5rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Pendientes</span>
                        <span style={{ background: 'rgba(255,255,255,0.1)', padding: '0.1rem 0.4rem', borderRadius: '12px' }}>2</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </nav>
          </div>
          
          {/* USER PROFILE CARD FOOTER */}
          <div className="mobile-hide" style={{ position: 'relative', zIndex: 1, marginTop: '1rem', paddingTop: '0.85rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(255,255,255,0.025)', padding: '0.6rem 0.7rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15), rgba(139, 92, 246, 0.15))', border: '1px solid rgba(0,229,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00e5ff', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>
                {userProfile?.name ? userProfile.name.charAt(0).toUpperCase() : <User size={16} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userProfile?.name || 'Miguel'}
                </div>
                <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap', fontWeight: 400 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#00e5ff', flexShrink: 0 }} />
                  Pro Member
                </div>
              </div>
              <button onClick={handleLogout} style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.18)', width: '30px', height: '30px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }} title="Cerrar sesión">
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </aside>
      )}
      
      <div className={appMode === 'athlete' || appMode === 'coach' ? 'dashboard-main-pro' : ''} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        
        {appMode === 'athlete' && (
          <div style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
            <h1 className="stagger-1" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', lineHeight: 1.1, fontSize: '2rem', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 800 }}>{getGreeting()},</span>
              <span className="text-gradient-premium" style={{ fontWeight: 800 }}>{userProfile?.name?.split(' ')[0] || 'Atleta'}</span>
            </h1>
            <p className="stagger-2" style={{
              fontSize: '0.95rem',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.4',
              fontStyle: 'italic',
              borderLeft: '3px solid var(--strava-orange)',
              paddingLeft: '0.75rem'
            }}>
              "{getLocalQuote()}"
            </p>
          </div>
        )}

      {adminToken && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          background: '#ef4444', color: 'white', padding: '0.75rem 1.5rem',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', fontWeight: 'bold',
          cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          letterSpacing: '0.05em'
        }}
        onClick={handleReturnToAdmin}
        >
          <User size={20} /> ESTÁS VIENDO LA PLATAFORMA COMO USUARIO - HAZ CLIC AQUÍ PARA VOLVER A ADMIN
        </div>
      )}
      
      {!(appMode === 'coach' && !selectedAthleteId) && (() => {
        return appMode === 'athlete' ? null : (
        <div 
          className={appMode === 'coach' || appMode === 'admin' ? "coach-detail-header" : "hero-header"}
        >
          <div className={appMode === 'admin' ? "admin-hero-content" : "hero-content"}>
            {successMsg && (
              <div style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #10b981', color: '#10b981', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backdropFilter: 'blur(5px)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}><Activity size={18} /> {successMsg}</span>
                <button onClick={() => setSuccessMsg(null)} style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}><X size={20}/></button>
              </div>
            )}
            {/* --- NUEVA BARRA SUPERIOR (HEADER) --- */}
            {appMode !== 'athlete' && (
            <header style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              paddingTop: appMode === 'coach' || appMode === 'admin' ? '0.5rem' : '1.5rem',
              marginBottom: appMode === 'coach' || appMode === 'admin' ? '0' : '1.5rem',
              width: '100%'
            }}>
              {/* Logo Izquierda */}
              <div style={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <NousLogo size={appMode === 'admin' ? 'nav' : 'small'} showSlogan={false} variant={appMode === 'coach' ? 'mentor' : appMode === 'admin' ? 'metrics' : 'soma'} />
                {appMode === 'coach' && selectedAthleteId && (
                  <>
                    <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.2)' }}></div>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>Ficha del Atleta</span>
                  </>
                )}
              </div>

              {/* Controles Derecha (Feedback, Notificaciones, Perfil) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {/* User Avatar Dropdown */}
                <div className="profile-dropdown-container">
                  <button 
                    className="tour-settings pulse-glow"
                    onClick={() => setIsAvatarMenuOpen(!isAvatarMenuOpen)}
                    style={{
                      width: '42px', height: '42px', borderRadius: '50%', border: '2px solid #ff5722',
                      background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', overflow: 'hidden', padding: 0,
                      boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)'
                    }}
                  >
                    <User size={22} color="white" />
                  </button>

                  {isAvatarMenuOpen && (
                    <div className="dropdown-menu">
                      <button className="dropdown-item danger" onClick={handleLogout}>
                        <LogOut size={16} /> Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>
            )}
          </div>
        </div>
        );
      })()}

      <div className={`main-content ${appMode === 'coach' ? 'theme-mentor' : 'theme-soma'} ${appMode === 'admin' ? 'admin-main-content' : ''}`}>
        {/* Contenido principal según modo */}
        {appMode === 'coach' && !selectedAthleteId ? (
            <CoachDashboard 
              clients={coachClients}
              loading={coachClientsLoading}
              onSelectAthlete={(athlete) => {
                setSelectedAthleteId(athlete.id);
                setSelectedAthleteObj(athlete);
              }}
              userProfile={userProfile}
              unreadCount={unreadChatCount}
              handleOpenMessaging={handleOpenMessaging}
              unreadChatCount={unreadChatCount}
              handleOpenNotifications={handleOpenNotifications}
              hasNotifications={hasNotifications}
              unreadBroadcastCount={unreadBroadcastCount}
              isOutdoorMode={isOutdoorMode}
              setIsOutdoorMode={setIsOutdoorMode}
              setShowSettingsModal={setShowSettingsModal}
              handleLogout={handleLogout}
              onStartTour={() => setRunCoachTour(true)}
            />
          ) : appMode === 'admin' ? (
          <AdminDashboard />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {appMode === 'coach' && selectedAthleteId && (
              <div style={{ marginBottom: '1rem' }}>
                <button onClick={() => { setSelectedAthleteId(null); setSelectedAthleteObj(null); }} style={{
                  background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '0.6rem 1.2rem',
                  borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }} onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.2)'} onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.1)'}>
                    ← Volver al listado de atletas
                  </button>
                </div>
              )}
              
              {appMode === 'coach' && selectedAthleteObj && (
                <AthleteHeader 
                  athlete={selectedAthleteObj} 
                  recoveryData={recoveryData} 
                  onAiClick={handleAiAnalyze}
                />
              )}

          {/* RENDERIZADO CONDICIONAL DE PESTAÑAS */}
          
          {appMode === 'coach' && selectedAthleteId ? (
            <div className="tab-pane dashboard-main" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <ErrorBoundary>
                {/* AI Fatigue Triage Alert (Auto-surfaced for at-risk athletes) */}
                {selectedAthleteObj && (selectedAthleteObj.recentStatus === 'Fatiga Alta' || (selectedAthleteObj.readinessScore && selectedAthleteObj.readinessScore < 40) || selectedAthleteObj.recentStatus === 'Recuperación' || (selectedAthleteObj.readinessScore && selectedAthleteObj.readinessScore < 70)) && (
                  <div className="stagger-1">
                    <CoachFatigueAlert 
                      athleteId={selectedAthleteObj.id} 
                      athleteName={selectedAthleteObj.name} 
                      readinessScore={selectedAthleteObj.readinessScore} 
                      recentStatus={selectedAthleteObj.recentStatus} 
                    />
                  </div>
                )}

                {/* Top Row: Readiness and Private Notes */}
                <div className="stagger-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                    <TrainingReadiness 
                      data={{
                        ...recoveryData,
                        ctl: recoveryData?.ctl ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.ctl : undefined),
                        atl: recoveryData?.atl ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.atl : undefined),
                        tsb: recoveryData?.tsb ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.tsb : undefined),
                        withingsConnected: isWithingsAuthenticated
                      }} 
                      userProfile={userProfile} 
                      isCoachView={true} 
                    />
                  <CoachAthleteMessaging 
                    athleteId={selectedAthleteId} 
                    athleteName={selectedAthleteObj?.name} 
                    currentUser={userProfile}
                    targetUser={selectedAthleteObj}
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    onMarkRead={handleMarkMessagesRead}
                  />
                </div>
                
                {/* Middle Row: Recent Activities */}
                {activities.length > 0 && (
                  <div className="stagger-2">
                    <RecentActivities activities={activities} athleteId={selectedAthleteId} />
                  </div>
                )}

                {/* Middle Row: Fitness Chart */}
                <div className="stagger-3">
                  <FitnessChart data={fitnessData} userId={selectedAthleteId} userProfile={userProfile} dashboardMode="advanced" recoveryData={recoveryData} />
                </div>
                
                {/* Bottom Row: Calendar */}
                <div className="stagger-4">
                  <TrainingCalendar 
                    activities={activities} 
                    plannedWorkouts={plannedWorkouts} 
                    appMode={appMode} 
                    athleteId={selectedAthleteId}
                    onSavePlanned={handleSavePlanned} 
                    onDeletePlanned={handleDeletePlanned} 
                    onSaveNotes={handleSaveNotes} 
                    onCompletePlanned={handleCompletePlanned} 
                  />
                </div>
              </ErrorBoundary>
            </div>
          ) : (
            <>
              {/* PESTAÑA 1: RENDIMIENTO Y CARGA */}
              {activeTab === 'rendimiento' && (
                <div className="tab-pane dashboard-main">
              {activities.length === 0 ? (
                <EmptyState 
                  isStravaAuthenticated={isStravaAuthenticated} 
                  userId={userProfile?.id} 
                  onSync={handleSyncFullHistory} 
                />
              ) : (
                <ErrorBoundary>
                  <Suspense fallback={<div style={{padding: '3rem', textAlign: 'center', color: '#888'}}>Cargando módulos de rendimiento...</div>}>
                    {/* SELECTOR DE EXPERIENCIA: MODO ESENCIAL vs MODO AVANZADO */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      flexWrap: 'wrap',
                      gap: '0.65rem',
                      padding: '0.6rem 0.9rem',
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '14px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Experiencia:</span>
                        <span className="mobile-hide" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                          {dashboardMode === 'essential' ? '⚡ Modo Esencial (Acción diaria y motivación)' : '🔬 Modo Avanzado (Telemetría, curvas y cinéticas de carga)'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', padding: '3px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <button
                          onClick={() => handleToggleDashboardMode('essential')}
                          style={{
                            padding: '4px 12px', borderRadius: '20px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                            background: dashboardMode === 'essential' ? 'linear-gradient(135deg, #00e5ff 0%, #0284c7 100%)' : 'transparent',
                            color: dashboardMode === 'essential' ? '#000' : 'var(--text-secondary)',
                            display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s'
                          }}
                        >
                          <Zap size={13} /> Esencial
                        </button>
                        <button
                          onClick={() => handleToggleDashboardMode('pro')}
                          style={{
                            padding: '4px 12px', borderRadius: '20px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                            background: dashboardMode === 'pro' ? 'linear-gradient(135deg, #fc4c02 0%, #dc2626 100%)' : 'transparent',
                            color: dashboardMode === 'pro' ? '#fff' : 'var(--text-secondary)',
                            display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s'
                          }}
                        >
                          <Activity size={13} /> Avanzado
                        </button>
                      </div>
                    </div>

                    {/* MINI READINESS CARD FOR MOBILE */}
                    {(() => {
                      if (!recoveryData || !recoveryData.readinessScore) return null;
                      const score = recoveryData.readinessScore;
                      let color = '#ef4444';
                      let label = 'Alarma';
                      let IconComp = Activity;
                      
                      if (score >= 90) { color = '#00e5ff'; label = 'Sobresaliente'; IconComp = Zap; }
                      else if (score >= 70) { color = '#10b981'; label = 'Óptimo'; IconComp = Activity; }
                      else if (score >= 35) { color = '#f59e0b'; label = 'Fatiga'; IconComp = Activity; }

                      return (
                        <div 
                          className="mobile-only-flex glass-panel stagger-1" 
                          style={{ 
                            marginBottom: 0, 
                            padding: '0.85rem 1.25rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            background: `linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.8))`,
                            cursor: 'pointer',
                            border: `1px solid ${color}40`,
                            boxShadow: `0 4px 20px ${color}15`,
                            borderRadius: '16px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onClick={() => {
                            const el = document.getElementById('training-readiness-module-athlete');
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }}
                        >
                          {/* Glow effect */}
                          <div style={{
                            position: 'absolute', top: '-50%', left: '-10%', width: '100px', height: '100px',
                            background: color, filter: 'blur(50px)', opacity: 0.15, borderRadius: '50%', zIndex: 0
                          }} />

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <IconComp size={16} color={color} />
                              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'white' }}>Predisposición</span>
                            </div>
                            <span style={{ fontSize: '0.8rem', color: color, fontWeight: 600, opacity: 0.9 }}>{label}</span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 1 }}>
                            <div style={{ position: 'relative', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg viewBox="0 0 36 36" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                <path
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke="rgba(255,255,255,0.15)"
                                  strokeWidth="3.5"
                                />
                                <path
                                  strokeDasharray={`${score}, 100`}
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke={color}
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'white', position: 'relative', zIndex: 1 }}>{score}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {/* ROW 1: Training Readiness 360° & Last Activity */}
                    <div className="stagger-1 bento-row" style={{ alignItems: 'flex-start' }}>
                      {visibleModules.includes('trainingReadiness') && (
                        <div id="training-readiness-module-athlete" style={{ flex: '1 1 340px', display: 'flex', minWidth: 0 }}>
                          <TrainingReadiness 
                            data={{
                              ...recoveryData,
                              ctl: recoveryData?.ctl ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.ctl : undefined),
                              atl: recoveryData?.atl ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.atl : undefined),
                              tsb: recoveryData?.tsb ?? (fitnessData && fitnessData.length > 0 ? fitnessData[fitnessData.length - 1]?.tsb : undefined),
                              withingsConnected: isWithingsAuthenticated
                            }} 
                            userProfile={userProfile} 
                            briefing={morningBriefing}
                            briefingLoading={briefingLoading}
                            isCoachView={appMode === 'coach'}
                            dashboardMode={dashboardMode}
                            phase={briefingPhase}
                            activities={activities}
                          />
                        </div>
                      )}
                      <div style={{ flex: '1.6 1 450px', display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: 0 }}>
                        {visibleModules.includes('lastActivity') && (
                          <LastActivity activities={activities} userProfile={userProfile} dashboardMode={dashboardMode} />
                        )}
                        <div className="stagger-2">
                          <Header activities={activities} />
                        </div>
                      </div>
                    </div>
                    {/* ROW 2: Heart Rate Zones (Modo Pro) */}
                    {dashboardMode === 'pro' && (
                      <div className="stagger-2">
                        <HeartRateDistribution data={stats.hrZones} />
                      </div>
                    )}
                    {/* ROW 3: Fitness/Fatigue Chart (full width) */}
                    {visibleModules.includes('fitnessChart') && <div className="stagger-3"><FitnessChart data={fitnessData} userId={selectedAthleteId} userProfile={userProfile} dashboardMode={dashboardMode} recoveryData={recoveryData} /></div>}
                    {/* PerformanceEvolution retirado de home para reducir fatiga visual */}
                    {/* ROW 4: Aerobic Efficiency (Modo Pro) */}
                    {dashboardMode === 'pro' && visibleModules.includes('aerobicEfficiency') && (
                      <div className="stagger-4" style={{ marginTop: '1.5rem' }}>
                        <AerobicEfficiency data={recoveryData} />
                      </div>
                    )}
                    {/* ROW 5: (Moved AI Report to Salud tab) */}
                    {/* ROW 6: KPI Summary cards at the bottom (Moved under LastActivity) */}
                  </Suspense>
                </ErrorBoundary>
              )}
            </div>
          )}

          {/* PESTAÑA 2: FISIOLOGÍA Y SALUD */}
          {activeTab === 'salud' && (
            <div className="tab-pane">
              <ErrorBoundary>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* ROW 1: (TrainingReadiness was here, moved to Rendimiento only) */}

                  {/* ROW 2: Diario de Sensaciones (Subido bajo readiness) */}
                  <div className="stagger-2" style={{ marginBottom: '1rem' }}>
                    <DailyFeedbackWidget 
                      date={new Date().toISOString().split('T')[0]} 
                      onSave={(text) => console.log('Saved feedback:', text)} 
                    />
                  </div>

                  {/* ROW 3: Sleep & Recovery (Sistema Nervioso) full-width */}
                  {visibleModules.includes('recoveryPanel') && (
                    <div className="stagger-3"><RecoveryPanel data={recoveryData} /></div>
                  )}
                  {/* ROW 4: Health metric cards in bento grid */}
                  {visibleModules.includes('healthSidebar') && (
                    <div className="stagger-4">
                      <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', marginTop: '1rem' }}>
                        <Heart size={20} style={{ color: 'var(--health-cyan)' }} />
                        Métricas de Salud Diaria
                      </h2>
                      <HealthSidebar 
                        healthData={healthData} 
                        appleHealthConnected={healthData?.appleHealthConnected ?? athletePreferences?.appleHealthConnected ?? false} 
                        googleHealthConnected={healthData?.googleHealthConnected ?? athletePreferences?.googleHealthConnected ?? false}
                        withingsConnected={healthData?.withingsConnected ?? isWithingsAuthenticated ?? false}
                        sleepData={recoveryData?.sleepData} 
                        rhrData={recoveryData?.rhrData} 
                        hrvData={recoveryData?.hrvData} 
                        withingsDebug={recoveryData?.withingsDebug} 
                        isGridLayout={true} 
                      />
                      
                      <div className="bento-row" style={{ marginTop: '1.5rem' }}>
                        <div style={{ flex: '1 1 300px' }}>
                          <BiomechanicsCard 
                            biomechanicsData={healthData?.biomechanics} 
                            sourceLabel={healthData?.googleHealthConnected ? 'Google Health' : (healthData?.appleHealthConnected ? 'Apple Health' : 'Sin sincronizar')} 
                          />
                        </div>
                        <div style={{ flex: '1 1 300px' }}><MetabolismCard metabolismData={healthData?.metabolism} /></div>
                      </div>
                    </div>
                  )}
                  
                </div>
              </ErrorBoundary>
            </div>
          )}

          {/* PESTAÑA 3: OBJETIVOS Y AGENDA */}
          {activeTab === 'objetivos' && (
            <div className="tab-pane dashboard-main">
              <ErrorBoundary>
                {/* ROW 1: Goals & Upcoming Events (Stacked Vertically) */}
                <div className="stagger-1" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '1.5rem', width: '100%' }}>
                  <div style={{ width: '100%' }}>
                    <GoalsWidget 
                      goalsData={goalsData} 
                      onOpenCreator={(goalToEdit) => {
                        setEditingGoal(goalToEdit || null);
                        setIsGoalCreatorOpen(true);
                      }} 
                      onDeleteGoal={handleDeleteGoal} 
                    />
                  </div>
                  <div style={{ width: '100%' }}>
                    <UpcomingEvents 
                      events={customEvents} 
                      onAddEvent={() => setIsEventModalOpen(true)} 
                      onDeleteEvent={handleDeleteEvent} 
                    />
                  </div>
                </div>
                {/* ROW 2: Training Calendar full width */}
                <div className="stagger-1" style={{ marginTop: '1.5rem', width: '100%' }}>
                  {visibleModules.includes('trainingCalendar') && <TrainingCalendar activities={activities} plannedWorkouts={plannedWorkouts} appMode={appMode} athleteId={userProfile?.id} onSavePlanned={handleSavePlanned} onDeletePlanned={handleDeletePlanned} onSaveNotes={handleSaveNotes} onCompletePlanned={handleCompletePlanned} />}
                </div>
                {/* ROW 3: Year-To-Date (Tu Año en Cifras) */}
                <div className="stagger-2" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                  <YearToDateWidget stats={stats} />
                </div>
                {/* ROW 4: Current Year Monthly Stats */}
                {visibleModules.includes('historicalStats') && (
                  <div className="stagger-3" style={{ marginBottom: '1.5rem' }}>
                    <CurrentYearMonthlyStats stats={stats} />
                  </div>
                )}
                {/* ROW 6: Historical Stats */}
                {visibleModules.includes('historicalStats') && (
                  <div className="stagger-4" style={{ marginBottom: '1.5rem' }}>
                    <HistoricalStats data={historyData} onSyncFull={handleSyncFullHistory} />
                  </div>
                )}
              </ErrorBoundary>
            </div>
          )}
          
          {/* HEADER KPIs (Movido a la parte inferior como barra de resumen global) */}
          {/* Header KPIs now inside the bento grid above */}
          </>
          )}
          </div>
        )}
      </div>
      </div>
      
      {/* El panel derecho ha sido eliminado para consolidar en 2 columnas */}

      {/* MOBILE BOTTOM NAVIGATION */}
      {appMode === 'athlete' && (
        <nav className="mobile-bottom-nav" style={{
          display: 'none',
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'rgba(11, 25, 44, 0.9)', backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: '0.8rem', paddingBottom: 'calc(0.2rem + env(safe-area-inset-bottom))',
          paddingLeft: '1rem', paddingRight: '1rem',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', maxWidth: '400px', margin: '0 auto' }}>
            <button onClick={() => setActiveTab('rendimiento')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: activeTab === 'rendimiento' ? 'var(--strava-orange)' : 'var(--text-secondary)' }}>
              <Activity size={24} />
              <span style={{ fontSize: '0.65rem' }}>Dashboard</span>
            </button>
            <button onClick={() => setActiveTab('objetivos')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: activeTab === 'objetivos' ? 'var(--health-green)' : 'var(--text-secondary)' }}>
              <Target size={24} />
              <span style={{ fontSize: '0.65rem' }}>Plan</span>
            </button>
            <button onClick={() => setActiveTab('salud')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: activeTab === 'salud' ? 'var(--health-cyan)' : 'var(--text-secondary)' }}>
              <Heart size={24} />
              <span style={{ fontSize: '0.65rem' }}>Salud</span>
            </button>
          </div>
          <style>{`
            @media (max-width: 768px) {
              .mobile-bottom-nav { display: block !important; }
              .mobile-fab-ai { display: flex !important; }
            }
          `}</style>
        </nav>
      )}

      {/* MOBILE AI COACH FLOATING BUTTON */}
      {appMode === 'athlete' && (
        <button 
          className="mobile-fab-ai"
          onClick={() => setIsAiDrawerOpen(true)}
          style={{
            display: 'none',
            position: 'fixed',
            bottom: 'calc(80px + env(safe-area-inset-bottom))',
            right: '20px',
            zIndex: 999,
            background: 'linear-gradient(135deg, #00e5ff 0%, #8b5cf6 100%)',
            border: 'none',
            borderRadius: '50%',
            width: '56px',
            height: '56px',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0, 229, 255, 0.4)',
            cursor: 'pointer',
            color: 'white'
          }}
        >
          <Sparkles size={24} />
        </button>
      )}
      
      {/* Tour de Ayuda Interactivo */}
      {appMode === 'athlete' && (
        <AthleteHelpTour 
          run={runTour} 
          onFinish={() => {
            setRunTour(false);
            localStorage.setItem('hasSeenTour', 'true');
          }} 
        />
      )}
      
      {appMode === 'coach' && (
        <CoachHelpTour 
          run={runCoachTour} 
          onFinish={() => setRunCoachTour(false)} 
        />
      )}

      {/* Asistente Deportivo IA Flotante */}
      <AICoachDrawer 
        isOpen={isAiDrawerOpen}
        onClose={() => setIsAiDrawerOpen(false)}
        athleteName={userProfile?.name} 
        upcomingEvents={customEvents} 
        suggestedInput={aiSuggestedInput} 
        setSuggestedInput={setAiSuggestedInput}
        recoveryData={recoveryData}
      />

      {showSettingsModal && (
        <AccountSettingsModal 
          isOpen={showSettingsModal} 
          onClose={() => {
            setShowSettingsModal(false);
            savePreferences(visibleModules, appMode);
          }} 
          visibleModules={visibleModules}
          setVisibleModules={setVisibleModules}
          appMode={appMode}
          setAppMode={setAppMode}
          userProfile={{
            ...userProfile,
            stravaConnected: isStravaAuthenticated,
            withingsConnected: isWithingsAuthenticated
          }}
          setUserProfile={(updaterOrProfile) => {
            if (typeof updaterOrProfile === 'function') {
              setUserProfile(prev => {
                const next = updaterOrProfile(prev);
                if (next && next.stravaConnected !== undefined) setIsStravaAuthenticated(Boolean(next.stravaConnected));
                if (next && next.withingsConnected !== undefined) setIsWithingsAuthenticated(Boolean(next.withingsConnected));
                return next;
              });
            } else {
              setUserProfile(updaterOrProfile);
              if (updaterOrProfile && updaterOrProfile.stravaConnected !== undefined) setIsStravaAuthenticated(Boolean(updaterOrProfile.stravaConnected));
              if (updaterOrProfile && updaterOrProfile.withingsConnected !== undefined) setIsWithingsAuthenticated(Boolean(updaterOrProfile.withingsConnected));
            }
          }}
        />
      )}

      {showFitUploadModal && (
        <FitUploadModal 
          isOpen={showFitUploadModal} 
          onClose={() => setShowFitUploadModal(false)} 
          onUploadSuccess={() => fetchAll()} 
        />
      )}
      
      {isMessagingOpen && (
        <MessagingPanel 
          isOpen={isMessagingOpen}
          onClose={() => setIsMessagingOpen(false)}
          currentUser={userProfile}
          targetUser={appMode === 'coach' 
              ? selectedAthleteObj 
              : (userProfile?.trainerRelation?.trainer || { id: messages.find(m => m.senderId !== userProfile?.id)?.senderId || 'admin', name: 'Centro de Mensajes', role: 'admin' })}
          messages={messages.filter(m => !m.content?.startsWith('SYSTEM_BROADCAST::'))}
          onSendMessage={handleSendMessage}
          onMarkRead={handleMarkMessagesRead}
        />
      )}

      <NotificationsPanel
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        notifications={messages.filter(m => m.content?.startsWith('SYSTEM_BROADCAST::'))}
        onMarkRead={handleMarkMessagesRead}
      />

      <AiAnalysisModal 
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        athleteName={selectedAthleteObj?.name}
        aiData={aiData}
        isAnalyzing={isAiAnalyzing}
      />

      <DailyHealthModal 
        isOpen={showDailyHealthModal} 
        onClose={() => setShowDailyHealthModal(false)} 
      />

      <EventModal 
        isOpen={isEventModalOpen} 
        onClose={() => setIsEventModalOpen(false)} 
        onSave={handleAddEvent} 
      />

      <BetaFeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
      />
      {isGoalCreatorOpen && <GoalCreatorModal 
          onClose={() => {
              setIsGoalCreatorOpen(false);
              setEditingGoal(null);
          }} 
          onGoalCreated={handleGoalCreated} 
          initialGoal={editingGoal}
      />}
      <WhatsNewAiModal 
        isOpen={showWhatsNewModal} 
        onClose={() => setShowWhatsNewModal(false)} 
      />
      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
        userProfile={userProfile}
        onSubscriptionSuccess={() => fetchAll()}
      />
      <CookieBanner />
    </div>
  );
}

export default App;
